import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SectionCard from "./SectionCard";
import { tokens } from "../tokens";

interface Props {
  templateName: string;
  onTemplateNameChange: (value: string) => void;
  relatedWebsite: string;
  componentType: string;
  /** Locked while a save is in progress. */
  disabled?: boolean;
}

const fieldLabelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: tokens.textMuted,
  mb: 0.9,
  display: "block",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: tokens.radius,
    fontSize: 13,
    backgroundColor: tokens.surface,
    "& fieldset": { borderColor: tokens.border },
    "&:hover fieldset": { borderColor: tokens.borderStrong },
    "&.Mui-focused fieldset": {
      borderColor: tokens.accent,
      borderWidth: "1px",
    },
  },
  "& .MuiOutlinedInput-input": { padding: "10px 12px" },
};

/**
 * Related Website and Component Type are not editable once a template exists.
 * They are rendered read-only rather than `disabled` so the value stays
 * readable — the current screen greys them out to the point of illegibility.
 */
const readOnlySx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    backgroundColor: tokens.bg,
    color: tokens.textMuted,
    "&:hover fieldset": { borderColor: tokens.border },
    "&.Mui-focused fieldset": {
      borderColor: tokens.border,
      borderWidth: "1px",
    },
  },
};

const GeneralCard = ({
  templateName,
  onTemplateNameChange,
  relatedWebsite,
  componentType,
  disabled = false,
}: Props) => (
  <SectionCard title="General">
    <Box sx={{ mb: 2.25 }}>
      <Typography component="label" sx={fieldLabelSx}>
        Template Name *
      </Typography>
      <TextField
        fullWidth
        value={templateName}
        onChange={(event) => onTemplateNameChange(event.target.value)}
        disabled={disabled}
        sx={inputSx}
      />
      <Typography sx={{ fontSize: 11.5, color: tokens.textFaint, mt: 0.75 }}>
        Minimum 8 characters, and must start with a letter.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.25 }}>
      <Typography component="label" sx={fieldLabelSx}>
        Related Website *
      </Typography>
      <TextField
        fullWidth
        value={relatedWebsite}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <KeyboardArrowDownIcon
                sx={{ fontSize: 16, color: tokens.textFaint }}
              />
            </InputAdornment>
          ),
        }}
        sx={readOnlySx}
      />
    </Box>

    <Box>
      <Typography component="label" sx={fieldLabelSx}>
        Component Type *
      </Typography>
      <TextField
        fullWidth
        value={componentType}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <KeyboardArrowDownIcon
                sx={{ fontSize: 16, color: tokens.textFaint }}
              />
            </InputAdornment>
          ),
        }}
        sx={readOnlySx}
      />
    </Box>
  </SectionCard>
);

export default GeneralCard;
